import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarDays, Camera, ChevronDown, ChevronLeft, Download, Edit, FileText, ImagePlus, IndianRupee, Send, Trash2, Upload, Wallet, X } from "lucide-react";
import { toast } from "sonner";

import { Layout } from "@/components/Layout";
import { HoardexaLoader } from "@/components/HoardexaLoader";
import { EmptyState, MetricStrip } from "@/components/CommandSystem";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { usePopup } from "@/components/AppPopups";
import api, { formatApiError } from "@/lib/api";
import { uploadToCloudinary } from "@/lib/cloudinary";
import { fmtDate, inr, inrPlain } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { hasAnyPermission, hasPermission } from "@/lib/permissions";
import { CampaignDialog } from "./CampaignDialog";
import {
  EMPTY,
  STATUS_BADGE,
  asArray,
  chargeFor,
  campaignChargeTotal,
  campaignContractTotal,
  validateCampaignMargins,
} from "./utils";

function dateOnly(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function canDeleteCampaign(campaign) {
  const start = dateOnly(campaign?.start_date);
  const today = dateOnly(new Date());
  if (start == null || today == null) return false;
  return start > today && !["Active", "Completed", "Expiring Soon"].includes(campaign?.status);
}

function InfoCard({ title, children, right }) {
  return (
    <section className="rounded-lg border border-slate-300 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-sm font-black text-slate-950">{title}</h2>
        {right}
      </div>
      <div className="mt-3">{children}</div>
    </section>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="grid grid-cols-[130px_minmax(0,1fr)] gap-3 border-b border-slate-100 py-2 last:border-0">
      <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
      <span className="min-w-0 truncate text-sm font-semibold text-slate-900">{value || "-"}</span>
    </div>
  );
}

function formatSize(hoarding) {
  if (!hoarding?.width || !hoarding?.height) return "";
  return `${hoarding.width} x ${hoarding.height}`;
}

function ReadOnlyChargeCell({ label, value }) {
  return (
    <div>
      <div className="text-xs font-bold text-slate-600">{label}</div>
      <div className="mt-1 h-8 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-bold text-slate-900">
        {inr(value)}
      </div>
    </div>
  );
}

function sanitizePaymentAmount(value, maxAmount) {
  const cleaned = String(value || "")
    .replace(/[^\d.]/g, "")
    .replace(/^(\d*\.)(.*)\./, "$1$2");
  const [wholeRaw, decimalRaw = ""] = cleaned.split(".");
  const whole = wholeRaw.replace(/^0+(?=\d)/, "");
  const decimal = decimalRaw.slice(0, 2);
  const normalized = cleaned.includes(".") ? `${whole || "0"}.${decimal}` : whole;
  if (!normalized) return "";
  const numeric = Number(normalized);
  if (!Number.isFinite(numeric)) return "";
  if (maxAmount > 0 && numeric > maxAmount) return String(maxAmount);
  return normalized;
}

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

function filenameFromResponse(response, fallback) {
  const disposition = response.headers?.["content-disposition"] || "";
  const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)"?/i);
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

function downloadBlob(response, fallback) {
  const blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: response.headers?.["content-type"] || "application/octet-stream" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filenameFromResponse(response, fallback);
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function invoiceStatusLabel(status) {
  if (status === "fully_paid") return "paid";
  if (status === "partially_paid") return "unpaid";
  return status || "unpaid";
}

function fullMoney(amount) {
  return inrPlain(Number(amount || 0));
}

function AmountWithGST({ amount, gstAmount, gstMode }) {
  if (gstMode !== "exclusive") return <>{fullMoney(amount)}</>;
  return (
    <span className="flex min-w-0 flex-col leading-tight">
      <span>{fullMoney(amount)}</span>
      <small className="mt-0.5 text-[11px] font-bold text-slate-500">+ {fullMoney(gstAmount)} GST</small>
    </span>
  );
}

function splitExclusiveAmount(finalAmount, baseAmount, gstAmount) {
  const final = Number(baseAmount || 0) + Number(gstAmount || 0);
  if (final <= 0) return { base: 0, gst: 0 };
  const ratio = Number(finalAmount || 0) / final;
  return {
    base: Math.max(0, Number(baseAmount || 0) * ratio),
    gst: Math.max(0, Number(gstAmount || 0) * ratio),
  };
}

function proofsByHoarding(proofs) {
  return asArray(proofs).reduce((acc, item) => {
    const key = item.hoarding_id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(item);
    return acc;
  }, {});
}

export function CampaignDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const popup = usePopup();
  const { user } = useAuth();
  const [campaign, setCampaign] = useState(null);
  const [finance, setFinance] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [invoiceBusy, setInvoiceBusy] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState({ amount_mode: "percentage", percentage: "50", amount: "", invoice_date: todayInput(), due_date: "", notes: "" });
  const [statusOpen, setStatusOpen] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [statusTarget, setStatusTarget] = useState(null);
  const [statusForm, setStatusForm] = useState({ status: "sent_to_client", payment_mode: "bank", payment_date: todayInput(), reference_number: "", notes: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(EMPTY);
  const [clients, setClients] = useState([]);
  const [hoardings, setHoardings] = useState([]);
  const [businessMargins, setBusinessMargins] = useState([]);
  const [accountingSettings, setAccountingSettings] = useState(null);
  const [hoardingSearch, setHoardingSearch] = useState("");
  const [proofs, setProofs] = useState([]);
  const [proofLoading, setProofLoading] = useState(false);
  const [proofOpen, setProofOpen] = useState(false);
  const [proofUploading, setProofUploading] = useState(false);
  const [proofForm, setProofForm] = useState({ hoarding_id: "", clicked_date: todayInput(), notes: "", photos: [] });
  const [expandedProofs, setExpandedProofs] = useState({});
  const [expandedChargeRows, setExpandedChargeRows] = useState(() => new Set());

  const canViewRevenue = hasAnyPermission(user, ["view_campaign_amounts", "view_basic_revenue", "view_profit_and_loss", "view_financial_reports"]);
  const canEdit = hasAnyPermission(user, ["edit_own_campaigns", "edit_assigned_campaigns", "edit_all_campaigns", "modify_records"]);
  const canUploadProofs = hasAnyPermission(user, ["upload_campaign_proofs", "edit_own_campaigns", "edit_assigned_campaigns", "edit_all_campaigns", "modify_records"]);
  const canManageInvoice = hasAnyPermission(user, ["manage_invoices", "manage_accounts"]);
  const canAssignHoardings = hasPermission(user, "assign_hoardings_to_campaign");
  const canChangeDates = hasPermission(user, "change_campaign_dates");
  const canEditAmounts = hasAnyPermission(user, ["edit_campaign_amounts", "change_campaign_amounts"]);
  const canDelete = hasPermission(user, "delete_campaigns") && canDeleteCampaign(campaign);
  const campaignBaseAmount = Number(campaign?.taxable_amount || 0);
  const campaignGSTAmount = Number(campaign?.gst_amount || 0);
  const campaignStoredFinalAmount = Number(campaign?.final_invoice_amount || 0);
  const campaignComputedFinalAmount = campaignBaseAmount + campaignGSTAmount;
  const campaignFinalAmount = campaignStoredFinalAmount > 0
    ? campaignStoredFinalAmount
    : campaignComputedFinalAmount > 0
      ? campaignComputedFinalAmount
      : Number(finance?.expected_amount || campaign?.revenue || 0);
  const paidInvoices = invoices.filter((invoice) => invoiceStatusLabel(invoice.payment_status) === "paid");
  const paidFinalAmount = paidInvoices.reduce((sum, invoice) => sum + Number(invoice.final_amount || 0), 0);
  const paidBaseAmount = paidInvoices.reduce((sum, invoice) => sum + Number(invoice.taxable_amount || 0), 0);
  const paidGSTAmount = paidInvoices.reduce((sum, invoice) => sum + Number(invoice.total_gst_amount || 0), 0);
  const billableInvoices = invoices.filter((invoice) => invoiceStatusLabel(invoice.payment_status) !== "cancelled");
  const billedFinalAmount = billableInvoices.reduce((sum, invoice) => sum + Number(invoice.final_amount || 0), 0);
  const billedBaseAmount = billableInvoices.reduce((sum, invoice) => sum + Number(invoice.taxable_amount || 0), 0);
  const billedGSTAmount = billableInvoices.reduce((sum, invoice) => sum + Number(invoice.total_gst_amount || 0), 0);
  const pendingPaymentInvoices = billableInvoices.filter((invoice) => invoiceStatusLabel(invoice.payment_status) !== "paid" && Number(invoice.pending_amount || 0) > 0.01);
  const paymentPendingBaseAmount = Math.max(0, campaignBaseAmount - paidBaseAmount);
  const paymentPendingGSTAmount = Math.max(0, campaignGSTAmount - paidGSTAmount);
  const paymentPendingAmount = campaign?.gst_mode === "exclusive"
    ? paymentPendingBaseAmount + paymentPendingGSTAmount
    : Math.max(0, campaignFinalAmount - paidFinalAmount);
  const paymentPendingSplit = campaign?.gst_mode === "exclusive"
    ? { base: paymentPendingBaseAmount, gst: paymentPendingGSTAmount }
    : { base: paymentPendingAmount, gst: 0 };
  const unbilledAmount = Math.max(0, campaignFinalAmount - billedFinalAmount);
  const unbilledSplit = campaign?.gst_mode === "exclusive"
    ? { base: Math.max(0, campaignBaseAmount - billedBaseAmount), gst: Math.max(0, campaignGSTAmount - billedGSTAmount) }
    : splitExclusiveAmount(unbilledAmount, campaignBaseAmount, campaignGSTAmount);
  const remainingInvoiceDisplayAmount = campaign?.gst_mode === "exclusive" ? unbilledSplit.base : unbilledAmount;
  const hasUnbilledAmount = unbilledAmount > 0.01;
  const hasPendingInvoicePayment = pendingPaymentInvoices.length > 0;

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/campaigns/${id}`, { skipGlobalLoader: true }),
      canViewRevenue ? api.get(`/finance-ops/campaigns/${id}/finance`, { skipGlobalLoader: true }).catch(() => ({ data: null })) : Promise.resolve({ data: null }),
      canViewRevenue ? api.get(`/accounts/campaigns/${id}/invoices`, { skipGlobalLoader: true }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ])
      .then(([campaignRes, financeRes, invoicesRes]) => {
        setCampaign(campaignRes.data || null);
        setFinance(financeRes.data || null);
        setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
      })
      .catch((err) => {
        toast.error(formatApiError(err.response?.data?.detail) || "Campaign could not be loaded");
        setCampaign(null);
      })
      .finally(() => setLoading(false));
  };

  const loadProofs = () => {
    setProofLoading(true);
    api.get(`/campaigns/${id}/proofs`, { skipGlobalLoader: true })
      .then(({ data }) => setProofs(Array.isArray(data) ? data : []))
      .catch(() => setProofs([]))
      .finally(() => setProofLoading(false));
  };

  useEffect(() => {
    load();
    loadProofs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, canViewRevenue]);

  useEffect(() => {
    api.get("/campaigns/init", { skipGlobalLoader: true }).then((r) => {
      const d = r.data || {};
      setHoardings(Array.isArray(d.hoardings) ? d.hoardings : []);
      setClients(Array.isArray(d.clients) ? d.clients : []);
      setBusinessMargins(Array.isArray(d.margins) ? d.margins : []);
    }).catch(() => {
      setHoardings([]);
      setClients([]);
      setBusinessMargins([]);
    });
    api.get("/accounts/settings", { skipGlobalLoader: true })
      .then(({ data }) => setAccountingSettings(data || null))
      .catch(() => setAccountingSettings(null));
  }, []);

  const duration = useMemo(() => {
    if (campaign?.is_open_ended) return "Open ended";
    const start = dateOnly(campaign?.start_date);
    const end = dateOnly(campaign?.end_date);
    if (start == null || end == null) return "-";
    return `${Math.max(1, Math.round((end - start) / 86400000) + 1)} days`;
  }, [campaign]);
  const groupedProofs = useMemo(() => proofsByHoarding(proofs), [proofs]);
  const proofCount = proofs.length;
  const assignedHoardings = asArray(campaign?.assigned_hoardings);
  const chargeBreakdown = useMemo(() =>
    assignedHoardings.map((hoarding) => {
      const charge = chargeFor(campaign?.contract_amounts?.[hoarding.id]);
      return {
        hoarding,
        charge,
        total: campaignChargeTotal(charge, campaign?.start_date, campaign?.end_date, campaign?.is_open_ended),
      };
    }),
    [assignedHoardings, campaign?.contract_amounts, campaign?.start_date, campaign?.end_date, campaign?.is_open_ended],
  );
  const pickableHoardings = useMemo(() =>
    hoardings
      .filter((h) => {
        if (!hoardingSearch) return true;
        const s = hoardingSearch.toLowerCase();
        return h.name?.toLowerCase().includes(s) || h.hoarding_id?.toLowerCase().includes(s);
      }),
    [hoardingSearch, hoardings],
  );
  const cities = useMemo(() => {
    return [...new Set(hoardings.map((h) => h.city).filter(Boolean))].sort();
  }, [hoardings]);

  function openEditDialog() {
    if (!campaign) return;
    nav(`/campaigns/${campaign.id}/edit`, { state: { campaign } });
  }

  async function saveEdit() {
    if (!editForm.name.trim()) { toast.error("Campaign name is required"); return; }
    if (!editForm.client_id) { toast.error("Client is required"); return; }
    if (!editForm.start_date) { toast.error("Start date is required"); return; }
    if (!editForm.is_open_ended && !editForm.end_date) { toast.error("End date is required unless this is open-ended."); return; }
    if (!editForm.is_open_ended && new Date(editForm.end_date) <= new Date(editForm.start_date)) { toast.error("End date must be after start date"); return; }
    if (editForm.hoarding_ids.length === 0) { toast.error("Select at least one hoarding"); return; }
    const marginError = validateCampaignMargins(editForm);
    if (marginError) { toast.error(marginError); return; }
    const contractTotal = campaignContractTotal(editForm, hoardings);
    try {
      const payload = {
        ...editForm,
        end_date: editForm.is_open_ended ? null : editForm.end_date,
        expected_end_date: editForm.expected_end_date || null,
        payment_due_date: editForm.payment_due_date || null,
        end_date_status: editForm.is_open_ended ? (editForm.end_date_status || "unknown") : (editForm.end_date_status || "confirmed"),
        po_value_status: editForm.po_value_status || "confirmed",
        estimated_daily_charge: Number(editForm.estimated_daily_charge || 0),
        estimated_monthly_charge: Number(editForm.estimated_monthly_charge || 0),
        estimated_po_value: Number(editForm.estimated_po_value || 0),
        confirmed_po_value: Number(contractTotal || 0),
        gst: {
          ...(editForm.gst || {}),
          gst_percentage: Number(editForm.gst?.gst_percentage || 0),
          cgst_percentage: Number(editForm.gst?.cgst_percentage || 0),
          sgst_percentage: Number(editForm.gst?.sgst_percentage || 0),
          igst_percentage: Number(editForm.gst?.igst_percentage || 0),
        },
      };
      if (!canAssignHoardings) delete payload.hoarding_ids;
      if (!canChangeDates) {
        delete payload.start_date;
        delete payload.end_date;
        delete payload.is_open_ended;
        delete payload.expected_end_date;
        delete payload.end_date_status;
      }
      if (!canEditAmounts) delete payload.contract_amounts;
      await api.put(`/campaigns/${campaign.id}`, payload);
      toast.success("Campaign updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Campaign could not be updated");
    }
  }

  function openInvoiceDialog() {
    if (!hasUnbilledAmount) {
      const pendingInvoice = pendingPaymentInvoices[0];
      if (pendingInvoice) {
        toast.info("Campaign is fully invoiced. Record payment against the pending invoice.");
        openStatusDialog(pendingInvoice, "paid");
        return;
      }
      toast.info("This campaign is already fully invoiced and paid");
      return;
    }
    const remainingInvoiceAmount = campaign?.gst_mode === "exclusive" ? unbilledSplit.base : unbilledAmount;
    setInvoiceForm({
      amount_mode: "amount",
      percentage: "",
      amount: String(Math.round(Math.max(0, remainingInvoiceAmount) * 100) / 100),
      invoice_date: todayInput(),
      due_date: campaign?.payment_due_date?.slice(0, 10) || "",
      notes: "",
    });
    setInvoiceOpen(true);
  }

  async function submitInvoice() {
    const payload = {
      amount_mode: invoiceForm.amount_mode,
      percentage: invoiceForm.amount_mode === "percentage" ? Number(invoiceForm.percentage || 0) : 0,
      amount: invoiceForm.amount_mode === "amount" ? Number(invoiceForm.amount || 0) : 0,
      invoice_date: invoiceForm.invoice_date || null,
      due_date: invoiceForm.due_date || null,
      notes: invoiceForm.notes,
    };
    if (payload.amount_mode === "percentage" && (payload.percentage <= 0 || payload.percentage > 100)) {
      toast.error("Enter percentage between 0 and 100");
      return;
    }
    if (payload.amount_mode === "amount" && payload.amount <= 0) {
      toast.error("Enter invoice amount");
      return;
    }
    setInvoiceBusy(true);
    try {
      await api.post(`/accounts/campaigns/${campaign?.id || id}/invoice`, payload);
      toast.success("Invoice created");
      setInvoiceOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invoice could not be created");
    } finally {
      setInvoiceBusy(false);
    }
  }

  function openStatusDialog(invoice, nextStatus = "sent_to_client") {
    setStatusTarget(invoice);
    setStatusForm({ status: nextStatus, payment_mode: "bank", payment_date: todayInput(), reference_number: "", notes: "" });
    setStatusOpen(true);
  }

  async function submitInvoiceStatus() {
    if (!statusTarget) return;
    if (statusForm.status === "paid" && !statusForm.payment_mode) {
      toast.error("Choose payment mode");
      return;
    }
    setStatusBusy(true);
    try {
      await api.post(`/accounts/invoices/${statusTarget.id}/status`, {
        status: statusForm.status,
        payment_mode: statusForm.status === "paid" ? statusForm.payment_mode : "",
        payment_date: statusForm.status === "paid" ? statusForm.payment_date : null,
        reference_number: statusForm.status === "paid" ? statusForm.reference_number : "",
        notes: statusForm.notes,
      });
      toast.success("Invoice status updated");
      setStatusOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invoice status could not be updated");
    } finally {
      setStatusBusy(false);
    }
  }

  async function downloadInvoicePDF(invoice) {
    try {
      const response = await api.get(`/accounts/invoices/${invoice.id}/download/pdf`, { responseType: "blob" });
      downloadBlob(response, `${invoice.invoice_number || "Invoice"}.pdf`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Invoice PDF could not be downloaded");
    }
  }

  async function deleteCampaign() {
    const reason = await popup.prompt({
      title: "Delete campaign",
      description: "Only future campaigns that have not started can be deleted. Add a reason for audit history.",
      label: "Reason",
      required: true,
      confirmText: "Continue",
      tone: "danger",
    });
    if (!reason) return;
    const confirmed = await popup.confirm({
      title: `Delete ${campaign?.campaign_id || "campaign"}?`,
      description: `Reason: ${reason}. This releases assigned hoardings and removes this campaign.`,
      confirmText: "Delete campaign",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await api.delete(`/campaigns/${id}`, { data: { reason } });
      toast.success("Campaign deleted");
      nav("/campaigns");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Campaign could not be deleted");
    }
  }

  function openProofDialog(hoarding) {
    setProofForm({
      hoarding_id: hoarding.id,
      clicked_date: todayInput(),
      notes: "",
      photos: [],
    });
    setProofOpen(true);
  }

  async function addProofFiles(files) {
    if (!files?.length) return;
    setProofUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        uploaded.push(await uploadToCloudinary(file, `hoardexa/campaign-proofs/${id}/`));
      }
      setProofForm((prev) => ({ ...prev, photos: [...prev.photos, ...uploaded] }));
      toast.success(`${uploaded.length} proof photo(s) uploaded`);
    } catch (err) {
      toast.error(err.message || "Proof photos could not be uploaded");
    } finally {
      setProofUploading(false);
    }
  }

  function removeProofPhoto(index) {
    setProofForm((prev) => ({ ...prev, photos: prev.photos.filter((_, i) => i !== index) }));
  }

  function toggleChargeRow(hoardingId) {
    setExpandedChargeRows((prev) => {
      const next = new Set(prev);
      if (next.has(hoardingId)) next.delete(hoardingId);
      else next.add(hoardingId);
      return next;
    });
  }

  async function submitProofs() {
    if (!proofForm.hoarding_id) { toast.error("Choose a hoarding"); return; }
    if (!proofForm.clicked_date) { toast.error("Select clicked date"); return; }
    if (proofForm.photos.length === 0) { toast.error("Upload at least one proof photo"); return; }
    setProofUploading(true);
    try {
      const { data } = await api.post(`/campaigns/${id}/proofs`, proofForm);
      setProofs(Array.isArray(data) ? data : []);
      setProofOpen(false);
      toast.success("Campaign proof saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Campaign proof could not be saved");
    } finally {
      setProofUploading(false);
    }
  }

  const actions = (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={() => nav("/campaigns")}>
        <ChevronLeft size={15} /> Back
      </Button>
      {canEdit && campaign && (
        <Button variant="outline" onClick={openEditDialog}>
          <Edit size={15} /> Edit
        </Button>
      )}
      {canManageInvoice && canViewRevenue && campaign && (
        <Button variant="outline" onClick={openInvoiceDialog} disabled={invoiceBusy}>
          {!hasUnbilledAmount && hasPendingInvoicePayment ? <Wallet size={15} /> : <FileText size={15} />}
          {invoiceBusy ? "Creating..." : !hasUnbilledAmount && hasPendingInvoicePayment ? "Collect Payment" : "Create Invoice"}
        </Button>
      )}
      {hasPermission(user, "delete_campaigns") && campaign && (
        <Button variant="destructive" disabled={!canDelete} onClick={deleteCampaign} title={canDelete ? "Delete campaign" : "Only future campaigns that have not started can be deleted"}>
          <Trash2 size={15} /> Delete
        </Button>
      )}
    </div>
  );

  return (
    <Layout title="Campaign detail" subtitle={campaign?.campaign_id || "Campaign workspace"} actions={actions}>
      {loading ? (
        <HoardexaLoader label="Loading campaign" />
      ) : !campaign ? (
        <EmptyState title="Campaign not found" description="The campaign could not be loaded or is outside your access scope." />
      ) : (
        <div className="hx-hoardings-page">
          <MetricStrip
            metrics={[
              { label: "Total Amount", value: <AmountWithGST amount={campaign?.gst_mode === "exclusive" ? campaignBaseAmount : campaignFinalAmount} gstAmount={campaignGSTAmount} gstMode={campaign?.gst_mode} />, icon: IndianRupee, tone: "indigo" },
              { label: "Received", value: <AmountWithGST amount={campaign?.gst_mode === "exclusive" ? paidBaseAmount : paidFinalAmount} gstAmount={paidGSTAmount} gstMode={campaign?.gst_mode} />, icon: Wallet, tone: "emerald" },
              { label: "Pending", value: <AmountWithGST amount={campaign?.gst_mode === "exclusive" ? paymentPendingSplit.base : paymentPendingAmount} gstAmount={paymentPendingSplit.gst} gstMode={campaign?.gst_mode} />, icon: IndianRupee, tone: "amber" },
              { label: "Duration", value: duration, icon: CalendarDays, tone: "slate" },
            ]}
          />

          <section className="grid grid-cols-1 gap-4">
            <InfoCard
              title="Campaign summary"
              right={(
                <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${STATUS_BADGE[campaign.status] || "border-slate-200 bg-slate-50 text-slate-600"}`}>
                  {campaign.status}
                </span>
              )}
            >
              <DetailRow label="Name" value={campaign.name} />
              <DetailRow label="Client" value={campaign.client_name} />
              <DetailRow label="Start" value={fmtDate(campaign.start_date)} />
              <DetailRow label="End" value={campaign.is_open_ended ? "Open ended" : fmtDate(campaign.end_date)} />
              <DetailRow label="End status" value={campaign.end_date_status} />
              <DetailRow label="PO value" value={campaign.po_value_status} />
              <DetailRow label="Payment due" value={fmtDate(campaign.payment_due_date)} />
            </InfoCard>

            {canViewRevenue && (
              <InfoCard
                title="Invoices"
                right={<span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-700">{invoices.length} generated</span>}
              >
                <div className="space-y-3">
                  {invoices.map((invoice) => {
                    const status = invoiceStatusLabel(invoice.payment_status);
                    const tone = status === "paid" ? "bg-emerald-50 text-emerald-700" : status === "sent_to_client" ? "bg-blue-50 text-blue-700" : status === "cancelled" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700";
                    return (
                      <div key={invoice.id} className="rounded-lg border border-slate-200 bg-white p-3">
                        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="font-mono text-sm font-black text-slate-950">{invoice.invoice_number}</span>
                              <span className={`rounded-full px-2 py-0.5 text-[11px] font-black ${tone}`}>{status.replaceAll("_", " ")}</span>
                            </div>
                            <div className="mt-1 text-xs font-semibold text-slate-500">
                              Invoice {fmtDate(invoice.invoice_date)} · Due {fmtDate(invoice.due_date)}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-3 lg:min-w-[420px]">
                            <div>
                              <div className="text-[10px] font-black uppercase text-slate-500">Taxable</div>
                              <div className="font-bold text-slate-950">{fullMoney(invoice.taxable_amount)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] font-black uppercase text-slate-500">GST</div>
                              <div className="font-bold text-slate-950">{fullMoney(invoice.total_gst_amount)}</div>
                            </div>
                            <div>
                              <div className="text-[10px] font-black uppercase text-slate-500">Total</div>
                              <div className="font-bold text-slate-950">{fullMoney(invoice.final_amount)}</div>
                            </div>
                          </div>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Button type="button" variant="outline" onClick={() => downloadInvoicePDF(invoice)} className="h-8">
                            <Download size={14} /> PDF
                          </Button>
                          {canManageInvoice && status !== "paid" && status !== "cancelled" && (
                            <Button type="button" variant="outline" onClick={() => openStatusDialog(invoice, "paid")} className="h-8">
                              <Wallet size={14} /> Mark paid
                            </Button>
                          )}
                          {canManageInvoice && status === "unpaid" && (
                            <Button type="button" variant="outline" onClick={() => openStatusDialog(invoice, "sent_to_client")} className="h-8">
                              <Send size={14} /> Sent to client
                            </Button>
                          )}
                          {canManageInvoice && status !== "paid" && status !== "cancelled" && (
                            <Button type="button" variant="outline" onClick={() => openStatusDialog(invoice, "cancelled")} className="h-8 border-rose-200 text-rose-700 hover:bg-rose-50">
                              <X size={14} /> Cancel
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {invoices.length === 0 && (
                    <EmptyState title="No invoices generated" description="Create an invoice for a partial or full campaign amount. Payment mode is captured later when the invoice is marked paid." />
                  )}
                </div>
              </InfoCard>
            )}

            {canViewRevenue && (
              <InfoCard
                title="Amount breakup"
                right={<span className="rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">{chargeBreakdown.length} selected</span>}
              >
                <div className="space-y-2">
                  {chargeBreakdown.map(({ hoarding, charge, total }) => {
                    const size = formatSize(hoarding);
                    const chargeOpen = expandedChargeRows.has(hoarding.id);
                    return (
                      <div
                        key={hoarding.id}
                        className="rounded-[10px] border border-blue-200 bg-blue-50/70 px-3 py-1.5 transition"
                      >
                        <div className="flex min-w-0 items-center gap-2.5">
                          <div className="min-w-0 flex-1">
                            <div className="flex min-w-0 flex-col gap-1.5 md:flex-row md:items-center md:justify-between">
                              <div className="min-w-0">
                                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[13px]">
                                  <span className="truncate text-[12px] font-extrabold text-[#0F172A]">{hoarding.hoarding_id || "No code"}</span>
                                  <h3 className="min-w-0 truncate font-bold text-[#0F172A]" title={hoarding.name}>{hoarding.name || "Untitled hoarding"}</h3>
                                  {hoarding.type && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-[#0F172A]">{hoarding.type}</span>}
                                  {hoarding.city && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-[#0F172A]">{hoarding.city}</span>}
                                  {size && <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-[#0F172A]">{size}</span>}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => toggleChargeRow(hoarding.id)}
                                className="shrink-0 rounded-lg px-2 py-1 text-left transition hover:bg-white/80 hover:ring-1 hover:ring-blue-200 md:text-right"
                                title={chargeOpen ? "Hide breakup" : "Show breakup"}
                              >
                                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">Total</div>
                                <div className="text-[13px] font-extrabold text-[#0F172A]">{inr(total)}</div>
                              </button>
                            </div>
                          </div>
                        </div>
                        {chargeOpen && (
                          <div className="mt-2 grid grid-cols-1 gap-2 border-t border-slate-200/60 pt-2 md:grid-cols-3">
                            <ReadOnlyChargeCell label="Display" value={charge.display} />
                            <ReadOnlyChargeCell label="Printing" value={charge.printing} />
                            <ReadOnlyChargeCell label="Mounting" value={charge.installation} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {chargeBreakdown.length === 0 && (
                    <EmptyState title="No charge breakup" description="Assign hoardings and campaign charges to see the per-hoarding breakup." />
                  )}
                </div>
              </InfoCard>
            )}
          </section>

          <section className="grid grid-cols-1 gap-4">
            <InfoCard title="Hoardings involved">
              <div className="overflow-hidden rounded-lg border border-slate-300">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-left text-[11px] font-black uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="px-3 py-2">ID</th>
                      <th className="px-3 py-2">Hoarding</th>
                      <th className="px-3 py-2">City</th>
                      <th className="px-3 py-2">Proof</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(campaign.assigned_hoardings || []).map((item) => (
                      <tr key={item.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="px-3 py-2 font-mono text-xs font-bold text-slate-500">{item.hoarding_id}</td>
                        <td className="px-3 py-2 font-semibold text-slate-900">{item.name}</td>
                        <td className="px-3 py-2 text-slate-600">{item.city || "-"}</td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-bold text-slate-600">{groupedProofs[item.id]?.length || 0}</span>
                            {canUploadProofs && (
                              <Button type="button" size="sm" variant="outline" onClick={() => openProofDialog(item)} className="h-8 gap-1.5">
                                <ImagePlus size={14} /> Add
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {(campaign.assigned_hoardings || []).length === 0 && (
                      <tr><td colSpan={4} className="px-3 py-8 text-center text-sm text-slate-400">No hoardings assigned.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </InfoCard>
          </section>

          {proofCount > 0 && (
          <InfoCard title="Campaign audit">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="text-sm font-bold text-slate-900">Proof photos</div>
                <p className="text-xs text-slate-500">{proofCount} dated photo{proofCount === 1 ? "" : "s"} saved for this campaign.</p>
              </div>
              {proofLoading && <span className="text-xs font-semibold text-slate-500">Loading proofs...</span>}
            </div>
            <div className="space-y-3">
              {(campaign.assigned_hoardings || []).map((hoarding) => {
                const rows = groupedProofs[hoarding.id] || [];
                const expanded = expandedProofs[hoarding.id] ?? true;
                return (
                  <div key={hoarding.id} className="rounded-lg border border-slate-200 bg-slate-50/60">
                    <button
                      type="button"
                      onClick={() => setExpandedProofs((prev) => ({ ...prev, [hoarding.id]: !expanded }))}
                      className="flex w-full flex-wrap items-center justify-between gap-2 px-3 py-3 text-left"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-black text-slate-900">{hoarding.name}</span>
                        <span className="block text-xs font-semibold text-slate-500">{hoarding.hoarding_id} | {hoarding.city || "-"}</span>
                      </span>
                      <span className="flex items-center gap-2 text-xs font-bold text-slate-600">
                        {rows.length} proof{rows.length === 1 ? "" : "s"} <ChevronDown size={15} className={expanded ? "rotate-180 transition" : "transition"} />
                      </span>
                    </button>
                    {expanded && (
                      <div className="border-t border-slate-200 p-3">
                        {rows.length === 0 ? (
                          <div className="rounded-lg border border-dashed border-slate-300 bg-white p-5 text-center">
                            <Camera className="mx-auto text-slate-300" size={26} />
                            <div className="mt-2 text-sm font-bold text-slate-700">No proof uploaded</div>
                            {canUploadProofs && <Button type="button" size="sm" onClick={() => openProofDialog(hoarding)} className="mt-3"><ImagePlus size={14} /> Add proof</Button>}
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                            {rows.map((proof) => (
                              <a key={proof.id} href={proof.photo_url} target="_blank" rel="noreferrer" className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
                                <img src={proof.photo_url} alt="" className="h-40 w-full bg-slate-100 object-cover" />
                                <div className="p-3">
                                  <div className="text-xs font-black uppercase tracking-wide text-slate-500">{fmtDate(proof.clicked_date)}</div>
                                  {proof.notes && <div className="mt-1 max-h-10 overflow-hidden text-sm font-semibold text-slate-900">{proof.notes}</div>}
                                  <div className="mt-2 text-[11px] text-slate-500">Uploaded {fmtDate(proof.created_at)}{proof.created_by_name ? ` by ${proof.created_by_name}` : ""}</div>
                                </div>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {(campaign.assigned_hoardings || []).length === 0 && (
                <EmptyState title="No campaign hoardings" description="Assign hoardings to this campaign before uploading proof photos." />
              )}
            </div>
          </InfoCard>
          )}
        </div>
      )}

      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="max-w-md hx-payment-dialog-compact">
          <DialogHeader>
            <DialogTitle>Create invoice</DialogTitle>
            <DialogDescription>
              Remaining invoice amount: {fullMoney(remainingInvoiceDisplayAmount)}. GST is {campaign?.gst_mode || "exclusive"}.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-1">
            <label className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Invoice by</Label>
              <Select value={invoiceForm.amount_mode} onValueChange={(amount_mode) => setInvoiceForm((prev) => ({ ...prev, amount_mode }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Percentage</SelectItem>
                  <SelectItem value="amount">Amount</SelectItem>
                </SelectContent>
              </Select>
            </label>
            {invoiceForm.amount_mode === "percentage" ? (
              <label className="space-y-1">
                <Label className="text-xs font-semibold text-slate-600">Percentage</Label>
                <Input value={invoiceForm.percentage} inputMode="decimal" placeholder="50" onChange={(e) => setInvoiceForm((prev) => ({ ...prev, percentage: sanitizePaymentAmount(e.target.value, 100) }))} />
              </label>
            ) : (
              <label className="space-y-1">
                <Label className="text-xs font-semibold text-slate-600">{campaign?.gst_mode === "exclusive" ? "Base amount" : "Inclusive amount"}</Label>
                <Input value={invoiceForm.amount} inputMode="decimal" placeholder="40000" onChange={(e) => setInvoiceForm((prev) => ({ ...prev, amount: sanitizePaymentAmount(e.target.value, campaign?.gst_mode === "exclusive" ? unbilledSplit.base : unbilledAmount) }))} />
              </label>
            )}
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1">
                <Label className="text-xs font-semibold text-slate-600">Invoice date</Label>
                <Input type="date" value={invoiceForm.invoice_date} onChange={(e) => setInvoiceForm((prev) => ({ ...prev, invoice_date: e.target.value }))} />
              </label>
              <label className="space-y-1">
                <Label className="text-xs font-semibold text-slate-600">Due date</Label>
                <Input type="date" value={invoiceForm.due_date} onChange={(e) => setInvoiceForm((prev) => ({ ...prev, due_date: e.target.value }))} />
              </label>
            </div>
            <label className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Notes</Label>
              <Textarea rows={2} value={invoiceForm.notes} onChange={(e) => setInvoiceForm((prev) => ({ ...prev, notes: e.target.value }))} />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvoiceOpen(false)}>Cancel</Button>
            <Button onClick={submitInvoice} disabled={invoiceBusy}>{invoiceBusy ? "Creating..." : "Create invoice"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="max-w-sm hx-payment-dialog-compact">
          <DialogHeader>
            <DialogTitle>Change invoice status</DialogTitle>
            <DialogDescription>{statusTarget?.invoice_number} · Pending {fullMoney(statusTarget?.pending_amount)}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-1">
            <label className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Status</Label>
              <Select value={statusForm.status} onValueChange={(status) => setStatusForm((prev) => ({ ...prev, status }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="sent_to_client">Sent to client</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </label>
            {statusForm.status === "paid" && (
              <>
                <label className="space-y-1">
                  <Label className="text-xs font-semibold text-slate-600">Payment mode</Label>
                  <Select value={statusForm.payment_mode} onValueChange={(payment_mode) => setStatusForm((prev) => ({ ...prev, payment_mode }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <label className="space-y-1">
                  <Label className="text-xs font-semibold text-slate-600">Payment date</Label>
                  <Input type="date" value={statusForm.payment_date} onChange={(e) => setStatusForm((prev) => ({ ...prev, payment_date: e.target.value }))} />
                </label>
                <label className="space-y-1">
                  <Label className="text-xs font-semibold text-slate-600">Reference number</Label>
                  <Input value={statusForm.reference_number} onChange={(e) => setStatusForm((prev) => ({ ...prev, reference_number: e.target.value }))} />
                </label>
              </>
            )}
            <label className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Notes</Label>
              <Textarea rows={2} value={statusForm.notes} onChange={(e) => setStatusForm((prev) => ({ ...prev, notes: e.target.value }))} />
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)}>Cancel</Button>
            <Button onClick={submitInvoiceStatus} disabled={statusBusy}>{statusBusy ? "Saving..." : "Save status"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={proofOpen} onOpenChange={setProofOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add campaign proof</DialogTitle>
            <DialogDescription>Upload dated proof photos for the selected hoarding.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-1">
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_180px]">
              <label className="space-y-1">
                <Label className="text-xs font-semibold text-slate-600">Hoarding</Label>
                <Select value={proofForm.hoarding_id} onValueChange={(hoarding_id) => setProofForm((prev) => ({ ...prev, hoarding_id }))}>
                  <SelectTrigger><SelectValue placeholder="Select hoarding" /></SelectTrigger>
                  <SelectContent>
                    {(campaign?.assigned_hoardings || []).map((item) => (
                      <SelectItem key={item.id} value={item.id}>{item.hoarding_id} - {item.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
              <label className="space-y-1">
                <Label className="text-xs font-semibold text-slate-600">Clicked date</Label>
                <Input type="date" value={proofForm.clicked_date} onChange={(e) => setProofForm((prev) => ({ ...prev, clicked_date: e.target.value }))} />
              </label>
            </div>

            <label className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Notes</Label>
              <Textarea
                rows={3}
                value={proofForm.notes}
                placeholder="Optional site proof note"
                onChange={(e) => setProofForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </label>

            <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
              <label className="flex cursor-pointer flex-col items-center justify-center rounded-lg bg-white px-4 py-6 text-center">
                <Upload size={22} className="text-blue-600" />
                <span className="mt-2 text-sm font-bold text-slate-900">{proofUploading ? "Uploading..." : "Choose proof photos"}</span>
                <span className="text-xs text-slate-500">Single or multiple photos are supported.</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  disabled={proofUploading}
                  onChange={(e) => {
                    addProofFiles(Array.from(e.target.files || []));
                    e.target.value = "";
                  }}
                />
              </label>
              {proofForm.photos.length > 0 && (
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  {proofForm.photos.map((photo, index) => (
                    <div key={`${photo.public_id || photo.url}-${index}`} className="relative overflow-hidden rounded-lg border border-slate-200 bg-white">
                      <img src={photo.url} alt="" className="h-24 w-full object-cover" />
                      <button type="button" onClick={() => removeProofPhoto(index)} className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-slate-700 shadow">
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProofOpen(false)}>Cancel</Button>
            <Button onClick={submitProofs} disabled={proofUploading || proofForm.photos.length === 0}>
              <Camera size={15} /> Save proof
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CampaignDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        editing={campaign}
        form={editForm}
        setForm={setEditForm}
        clients={clients}
        cities={cities}
        allHoardings={hoardings}
        pickableHoardings={pickableHoardings}
        hoardingSearch={hoardingSearch}
        setHoardingSearch={setHoardingSearch}
        canAssignHoardings={canAssignHoardings}
        canChangeDates={canChangeDates}
        canEditAmounts={canEditAmounts}
        canViewRevenue={canViewRevenue}
        businessMargins={businessMargins}
        accountingSettings={accountingSettings}
        bookingConflicts={[]}
        onSave={saveEdit}
      />
    </Layout>
  );
}
